import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, object = authed, false = not authed

  const setToken = (t) => {
    if (t) localStorage.setItem("crm_token", t);
    else localStorage.removeItem("crm_token");
  };

  const checkAuth = useCallback(async () => {
    if (!localStorage.getItem("crm_token")) { setUser(false); return; }
    try {
      const u = await api.me();
      setUser(u);
    } catch {
      setToken(null);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, let the callback exchange the session first.
    if (window.location.hash?.includes("session_id=")) {
      (async () => {
        try {
          const sessionId = window.location.hash.split("session_id=")[1].split("&")[0];
          const res = await api.googleSession(sessionId);
          setToken(res.token);
          setUser(res.user);
        } catch {
          setUser(false);
        } finally {
          // strip the fragment so it can't be reused
          window.history.replaceState(null, "", window.location.pathname);
        }
      })();
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.login(email, password);
    setToken(res.token);
    setUser(res.user);
    return res.user;
  };

  const logout = () => {
    setToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) navigate("/crm/login", { replace: true });
  }, [user, navigate]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface text-mutedText" data-testid="crm-loading">
        Loading your dashboard…
      </div>
    );
  }
  if (!user) return null;
  return children;
}
